/** Game state, actions and the redacted per-player view. Shared by engine, server and client. */
import { z } from 'zod';

export type GameMode = '1v1' | '2v2';
export const MODE_RULES: Record<GameMode, { players: number; winLevel: number; turnLimit: number }> = {
  '1v1': { players: 2, winLevel: 10, turnLimit: 30 },
  '2v2': { players: 4, winLevel: 14, turnLimit: 30 },
};
export const PARTY_MAX = 5;
export const HAND_MAX = 6;
/** The opening party is dealt from fighters at or below this level (when the deck has enough), so nobody starts miles ahead. */
export const OPENING_MAX_LEVEL = 5;
/** King Plum's on-kill roll can make the opponent discard a fighter at most this many times a game. */
export const KING_PLUM_MAX_USES = 4;
/** Default battle clock in seconds (0 = none). Admins can change it in Game settings; async matches never have one. */
export const DEFAULT_TIME_LIMITS: Record<GameMode, number> = { '1v1': 600, '2v2': 1080 };

export interface Fighter {
  /** stable identity for animations; unique within a game */
  uid: number;
  slug: string;
  exhausted: boolean;
  bonus: number;
  insured?: boolean;
  stunned?: boolean;
  /** a Corrupted or sun fighter whose ability can still be used (once each time it enters a party) */
  ready?: boolean;
  /** Flaming Cultist: loses 1 level at the start of each of its owner's turns (never below 0) */
  burning?: boolean;
  /** Bubble: its level can't be lowered until its owner's next turn */
  shielded?: boolean;
}

export interface PlayerState {
  id: string;
  name: string;
  avatar: string;
  ai: boolean;
  team: number;
  seat: number;
  gooflord: string;
  party: Fighter[];
  fighterDeck: string[];
  itemDeck: string[];
  hand: string[];
  /** items are stored as "__item:<slug>" so one pile holds both kinds */
  discard: string[];
  kills: number;
  tames: number;
  usedAbility: boolean;
  usedGameAbility: boolean;
  itemUsed: boolean;
  itemBlocked: boolean;
  /** one party sacrifice per turn */
  sacrificed?: boolean;
  /** this-turn buffs on your own fighters, keyed by fighter uid */
  buffs: Record<string, number>;
  /** this-turn level loss on enemy fighters you attack, keyed by the enemy fighter's uid */
  targetDebuff: Record<string, number>;
  debuff: number;
  rollBonus: number;
  skipNext: boolean;
  /** item hand limit when it isn't HAND_MAX: only in games saved while Little Jimbo's players could hold 10 */
  handMax?: number;
  /** King Plum fighter discards so far (capped at KING_PLUM_MAX_USES) */
  plumUses?: number;
  /** turns still to be skipped after skipNext (Corrupted Booger Ghost) */
  skipTurns?: number;
  /** Corrupted King Eye: this hand is shown to everyone until the end of this round number */
  revealUntil?: number;
}

/**
 * A private decision the engine is waiting on before the turn can go on: look at cards (peek), put them in an order
 * (order), or take one (steal). Only the chooser sees the options; everyone else gets an empty list.
 */
export interface Choice {
  player: number;
  kind: 'peek' | 'order' | 'steal';
  /** whose pile the options come from */
  from: number;
  pile: 'item' | 'fighter' | 'hand';
  options: string[];
  /** how many picks the answer needs: 0 for peek, options.length for order, 1 for steal */
  count: number;
  /** the card that asked, for the prompt */
  source: string;
}

export interface TeamState { level: number; kills: number }

export interface LogEntry { id: number; turn: number; text: string }

/** Structured events emitted by one action, for animation and sound. Cleared at the start of every act(). */
export type GameEvent =
  | { kind: 'wild'; player: number; slug: string }
  | { kind: 'draw_fighter'; player: number; slug: string; uid: number }
  /** slug is omitted in views for seats that can't see this player's hand */
  | { kind: 'draw_item'; player: number; slug?: string }
  | { kind: 'tame'; player: number; slug: string; sum: number; need: number; ok: boolean }
  | { kind: 'keep'; player: number; slug: string; kept: boolean }
  | { kind: 'sacrifice'; player: number; uid: number; slug: string }
  | { kind: 'raid'; player: number; attacker: number; targetPlayer: number; target: number; ap: number; dp: number; result: 'kill' | 'die' | 'tie' }
  | { kind: 'raid_gooflord'; player: number; attacker: number; targetPlayer: number }
  | { kind: 'kill'; player: number; victimPlayer: number; uid: number; slug: string; gain: number }
  | { kind: 'item'; player: number; slug: string; targetPlayer?: number; target?: number }
  | { kind: 'ability'; player: number; ability: string }
  | { kind: 'roll'; player: number; value: number; need?: number; ok?: boolean; reason: string }
  | { kind: 'coins'; player: number; heads: number }
  | { kind: 'level'; team: number; delta: number; level: number }
  | { kind: 'buff'; player: number; uid: number; delta: number }
  | { kind: 'discard'; player: number; slug: string; item: boolean }
  | { kind: 'turn'; player: number; turnNo: number }
  | { kind: 'skip'; player: number }
  | { kind: 'extra_turn'; player: number }
  | { kind: 'win'; team: number; reason: string }
  | { kind: 'forfeit'; player: number };

export interface GameState {
  mode: GameMode;
  seq: number;
  turn: number;
  /** index of the player who took the first turn; a new round starts when the turn returns to them */
  first: number;
  turnNo: number;
  phase: 'act' | 'keep' | 'choose';
  /** set while phase is 'choose' */
  choice?: Choice | null;
  wild: string | null;
  pendingWild: string | null;
  players: PlayerState[];
  teams: TeamState[];
  log: LogEntry[];
  events: GameEvent[];
  winner: number | null;
  winReason: string | null;
  seed: number;
  rng: number;
  nextUid: number;
  nextLog: number;
}

// ---- actions (intents). The server validates these with zod before the engine sees them.
const idx = z.number().int().min(0).max(63);
export const ActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('item'), hand: idx, targetPlayer: idx.optional(), target: idx.optional() }),
  z.object({ type: z.literal('tame'), fighters: z.array(idx).min(1).max(PARTY_MAX) }),
  z.object({ type: z.literal('keep'), keep: z.boolean() }),
  z.object({ type: z.literal('skipWild') }),
  z.object({ type: z.literal('sacrifice'), fighter: idx }),
  z.object({ type: z.literal('raid'), attacker: idx, targetPlayer: idx.optional(), target: idx.optional() }),
  z.object({ type: z.literal('ability'), target: idx.optional(), targetPlayer: idx.optional(), picks: z.array(idx).max(5).optional() }),
  /** tap a party fighter's Corrupted or sun ability */
  z.object({ type: z.literal('corrupted'), fighter: idx, target: idx.optional(), targetPlayer: idx.optional(), picks: z.array(idx).max(5).optional() }),
  z.object({ type: z.literal('choose'), picks: z.array(idx).max(10) }),
  z.object({ type: z.literal('end') }),
]);
export type Action = z.infer<typeof ActionSchema>;

export interface ActResult { ok: boolean; error?: string }

/** What one seat is allowed to see. Opponents' hands and every deck order are hidden. */
export interface PlayerView extends Omit<PlayerState, 'fighterDeck' | 'itemDeck' | 'hand'> {
  fighterDeckCount: number;
  itemDeckCount: number;
  hand: string[] | null;
  handCount: number;
  connected?: boolean;
}
export interface GameView extends Omit<GameState, 'players' | 'rng' | 'seed'> {
  players: PlayerView[];
  me: number;
  turnEndsAt: number | null;
  /** when the battle clock runs out (ms since epoch), or null when there is no clock */
  matchEndsAt: number | null;
}

export interface DeckInput {
  player: { id: string; name: string; avatar: string };
  gooflord: string;
  fighters: string[];
  items: string[];
  ai?: boolean;
}
